import { createContext, useState, useEffect, useMemo, useRef } from "react";
import { request } from "../../shared/utils/network"; // Ensure request is properly imported
import { useDispatch, useSelector } from "react-redux";
import { updateMetaData } from "../../store/slices/metaDataSlice";
import { STORAGE_KEYS } from "../../shared/utils/constants";


import { readToken } from '../../shared/auth/session';

export const GlobalContext = createContext();

export const GlobalProvider = ({ children }) => {
  const dispatch = useDispatch();
  const metaData = useSelector((state) => state.metaData);

  // Ref to store sidebar toggle function (for mobile hamburger menu)
  const appSidebarToggleRef = useRef(null); // For App-level sidebar (Dashboard, Projects, etc.)
  const hasAutoSelectedFirstApi = useRef(false); // Flag to prevent repeated auto-selection

  // Helper to build a user-scoped storage key for persisting last selected API
  const getLastApiStorageKey = () => {
    const userId = sessionStorage.getItem(STORAGE_KEYS.USERID);
    if (userId) {
      return `${STORAGE_KEYS.LAST_SELECTED_API}_${userId}`;
    }
    // Fallback: try to extract from JWT token
    const token = readToken();
    if (!token) return STORAGE_KEYS.LAST_SELECTED_API;
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const uid = payload?.user_id || payload?.sub || payload?.id || payload?.email;
      return `${STORAGE_KEYS.LAST_SELECTED_API}_${uid || 'anon'}`;
    } catch (e) {
      return STORAGE_KEYS.LAST_SELECTED_API;
    }
  };

  const [scriptType, setScriptType] = useState("Pre-request");
  const [allowedProjectIds, setallowedProjectIds] = useState([]);
  const [activeTab, setActiveTab] = useState("API");
  const [headerKeyValue, setHeaderKeyValue] = useState([]);
  const [checkedRowsData, setCheckedRowsData] = useState([]);
  const [AuthorizationScriptDetails, setAuthorizationScriptDetails] =
    useState();
  let [bearerValue, setBearerValue] = useState(null);
  let [basicAuthValue, setBasicAuthValue] = useState({
    username: null,
    password: null,
  });
  let [checkedRows, setCheckedRows] = useState([]);
  let [localRows, setLocalRows] = useState([]);
  let [sectionTracker, setsectionTracker] = useState(null); // To check in single API currently we have opened authorization,header,body .
  let [description, setDescription] = useState(null);
  let [requestId, setRequestId] = useState("");
  const [singleApiRunData, setSingleApiRunData] = useState({
    payload: {
      method: null,
      request_url: null,
      request_header: [],
      request_query_parameters: [],
      request_body: {
        type: null,
        structure: null,
        value: {},
      },
      metadata: {
        request_id: requestId || null,
        environment_id: null,
        dataset_id: null,
        test_duration: null,
        expected_results: null,
      },
    },
  });


  // Fixed 22 Aug 2026: this was a bare setTimeout in the component body,
  // scheduling a fresh timer on every render. An effect persists the same
  // snapshot, once per actual metaData change.
  useEffect(() => {
    sessionStorage.setItem(STORAGE_KEYS.METADATA, JSON.stringify(metaData));
  }, [metaData]);

  const [datasetName, setDatasetName] = useState("");
  const [response, setResponse] = useState(null);
  const [loaderStatus, setLoaderStatus] = useState("");

  const [isLoading, setIsLoading] = useState(false);

  const [scriptContent, setScriptContent] = useState("");

  const [selectedMethod, setSelectedMethod] = useState(
    metaData?.request_body?.method_name
  );
  const [positionApi, setPositionApi] = useState(null);
  const [activeAuthorization, setActiveAuthorization] = useState(null);
  const [activeDashBoard, setActiveDashboard] = useState("");
  const [projects, setProjects] = useState();
  const [pendingRequests, setPendingRequests] = useState([]);
  const [editorContent, setEditorContent] = useState(
    metaData?.request_body?.request_body || ""
  );
  const [orchestrations, setOrchestrations] = useState([]);
  const [triggerHeader, setTriggerHeader] = useState(false);
  const [projectData, setProjectData] = useState();
  const [selectedDataset, setSelectedDataset] = useState("");

  // Auto-sync orchestrations to sessionStorage when it changes
  useEffect(() => {
    if (orchestrations && orchestrations.length > 0) {
      sessionStorage.setItem(STORAGE_KEYS.ORCHESTRATIONS, JSON.stringify(orchestrations));
    }
  }, [orchestrations]);

  // One mirror for the projects cache (22 Aug 2026). Twenty-six call sites
  // used to write sessionStorage by hand next to their setProjects call —
  // every hand-rolled copy is a stale-cache bug waiting to happen. State is
  // the source of truth; this effect is the only writer.
  useEffect(() => {
    if (projects !== undefined) {
      sessionStorage.setItem(STORAGE_KEYS.PROJECTS, JSON.stringify(projects));
    }
  }, [projects]);

  // let navigate=useNavigate();

  // Fetch Projects Function
  /**
   * Shape a project for the tree: every node starts collapsed and not editing.
   * A summary response has no collections, which the tree renders as a project
   * with nothing under it yet — the same thing it shows while a project is
   * genuinely empty.
   */
  const decorateProject = (project) => ({
    ...project,
    isOpen: false,
    editing: false,
    collections: (project.collections || []).map((collection) => ({
      ...collection,
      isOpen: false,
      editing: false,
      folders: (collection.folders || []).map((folder) => ({
        ...folder,
        isOpen: false,
        editing: false,
        sub_folders: (folder.sub_folders || []).map((sub_folder) => ({
          ...sub_folder,
          isOpen: false,
          editing: false,
        })),
      })),
    })),
  });

  /** The two-row shape the legacy dashboard slot expects. */
  const toProjectRows = (list) =>
    (list || []).slice(0, 2).map((project) => ({
      projectName: project?.name,
      poc: project?.ownerName,
      members: project?.memberCount,
      apiCount: project?.apiCount,
    }));

  /**
   * Load projects in two passes.
   *
   * The tree renders every project collapsed, so the full hierarchy is not
   * needed to paint it — but the app was downloading all of it before showing
   * anything. Measured on one project with one collection: 228 KB and 190ms
   * for the hierarchy against 386 bytes and 8ms for the summary, and the
   * hierarchy grows with the organisation's total content rather than its
   * project count.
   *
   * So: summary first, so the sidebar appears immediately, then the hierarchy
   * in the background to fill it in. The end state is byte-for-byte what it
   * was before — the same objects, cached the same way — which is what keeps
   * this safe for the many places that read `projects` from this context.
   * The only new intermediate state is a project whose collections have not
   * arrived, which renders exactly like an empty one.
   *
   * @param {boolean} forceRefresh  skip the cache and go to the server.
   *
   * Anything that has just CHANGED the tree server-side must pass true. The
   * cache is the whole tree, so a caller that mutates it and then calls this
   * without the flag gets its own change reverted: React has not flushed the
   * mirroring effect above yet, so the read returns the pre-change tree and
   * setProjects puts it back. That is precisely what happened to an imported
   * Postman collection — appended optimistically, then overwritten a line
   * later by a cache that predated it, so a successful import rendered as
   * nothing at all.
   *
   * Mirrors fetchOrchestrations(forceRefresh), which has had this since it was
   * written.
   */
  const fetchProjects = async (forceRefresh = false) => {
    try {
      setIsLoading(true);
      const cachedData = sessionStorage.getItem(STORAGE_KEYS.PROJECTS);
      const cachedOrchestration = sessionStorage.getItem(STORAGE_KEYS.ORCHESTRATIONS);

      if (!forceRefresh && cachedData && JSON.parse(cachedData).length > 0) {
        const parsedProjects = JSON.parse(cachedData) || [];
        setProjects(parsedProjects);
        setProjectData(toProjectRows(parsedProjects));
        if (cachedOrchestration && JSON.parse(cachedOrchestration).length > 0) {
          setOrchestrations(JSON.parse(cachedOrchestration));
        }
        return;
      }

      // Pass 1 — names only, so the sidebar stops waiting on the tree.
      try {
        const summaryRes = await request("/api/project/v2/getProjectsSummary", "POST", { payload: {} });
        const summary = (summaryRes?.data?.projects || []).map(decorateProject);
        if (summary.length > 0) {
          setProjects(summary);
          setProjectData(toProjectRows(summary));
        }
      } catch (err) {
        // Non-fatal: the hierarchy below is the real load, and it carries the
        // same fields. A failed summary costs the early paint, nothing else.
        console.warn("Project summary unavailable, waiting on the full hierarchy:", err?.message);
      }

      // Pass 2 — the hierarchy the tree expands into.
      const response1 = await request("/api/project/v2/getAllProjects", "POST", { payload: {} });
      const updatedProjects = (response1?.data?.projects || []).map(decorateProject);

      setProjects(updatedProjects);
      setProjectData(toProjectRows(updatedProjects));

      const fetchAuthorizationTypes = async () => {
        try {
          const response = await request("/api/authorization");
          setAllAuthorization(response?.data?.authorization || []);
        } catch (err) {
          console.error("Error fetching authorization types:", err);
        }
      };

      if (readToken()) {
        fetchAuthorizationTypes();
      }
    } catch (err) {
      console.error("Error fetching projects or orchestrations:", err);
    } finally {
      setIsLoading(false);
    }
  };

  // useEffect(() => {
  //   const cachedData = JSON.parse(sessionStorage.getItem(STORAGE_KEYS.METADATA));
  //   console.log(cachedData)
  //   if (cachedData) {
  //     dispatch(updateMetaData(cachedData));
  //   }
  // }, []);

  // Fetch Orchestrations Function - Called on demand
  // respects cached data unless forceRefresh is true
  const fetchOrchestrations = async (forceRefresh = false) => {
    try {
      // Check sessionStorage first unless force refresh is requested
      if (!forceRefresh) {
        const cachedData = sessionStorage.getItem(STORAGE_KEYS.ORCHESTRATIONS);
        if (cachedData) {
          try {
            const parsedData = JSON.parse(cachedData);
            if (parsedData && parsedData.length > 0) {
              setOrchestrations(parsedData);
              return;
            }
          } catch (e) {
            console.warn("Failed to parse cached orchestrations:", e);
          }
        }
      }

      const response = await request(
        "/api/orchestration/getAllOrchestrations",
        "POST",
        { payload: {} }
      );
      const allOrchestrations = response?.data?.orchestrations || [];
      setOrchestrations(allOrchestrations);
      sessionStorage.setItem(
        STORAGE_KEYS.ORCHESTRATIONS,
        JSON.stringify(allOrchestrations)
      );
    } catch (err) {
      console.error("Error fetching orchestrations:", err);
    }
  };

  // Fetch Pending Requests
  let [pendingRequestForTable, setPendingRequestForTable] = useState();
  const fetchPendingRequests = async () => {
    try {
      const token = readToken();
      const headers = { Authorization: `Bearer ${token}` };
      const response = await request(
        "/api/project/getPendingRequest",
        "POST",
        null,
        "application/json",
        headers
      );

      setPendingRequests(response.data || []);
      setPendingRequestForTable([
        {
          name: response?.data[0]?.name,
          requested_by: response?.data?.[0]?.requested_by,
        },
        {
          name: response?.data[1]?.project_name,
          requested_by: response?.data?.[1]?.requested_by,
        },
      ]);
    } catch (error) {
      console.error("Error fetching pending requests:", error);
      setPendingRequests([]);
    }
  };

  // Effect Hooks
  useEffect(() => {
    if (metaData?.belong_to !== undefined) {
      dispatch(
        updateMetaData({
          api_url: null,
          request_body: null,
        })
      );
    }
  }, [metaData?.belong_to, dispatch]);

  // Helper function to find an API by request_id in projects
  const findApiInProjects = (projectsList, requestId) => {
    for (const project of projectsList) {
      if (!project.collections) continue;
      
      for (const collection of project.collections) {
        // Check collection requests
        const foundInCollection = collection.requests?.find(r => r.request_id === requestId);
        if (foundInCollection) {
          return { project, collection, folder: null, subFolder: null, api: foundInCollection };
        }
        
        // Check folders
        if (collection.folders) {
          for (const folder of collection.folders) {
            const foundInFolder = folder.requests?.find(r => r.request_id === requestId);
            if (foundInFolder) {
              return { project, collection, folder, subFolder: null, api: foundInFolder };
            }
            
            // Check sub-folders
            if (folder.sub_folders) {
              for (const subFolder of folder.sub_folders) {
                const foundInSubFolder = subFolder.requests?.find(r => r.request_id === requestId);
                if (foundInSubFolder) {
                  return { project, collection, folder, subFolder, api: foundInSubFolder };
                }
              }
            }
          }
        }
      }
    }
    return null;
  };

  // Helper function to find the first available API in projects
  const findFirstApiInProjects = (projectsList) => {
    for (const project of projectsList) {
      if (!project.collections || project.collections.length === 0) continue;
      
      for (const collection of project.collections) {
        // Check collection requests first
        if (collection.requests && collection.requests.length > 0) {
          return { project, collection, folder: null, subFolder: null, api: collection.requests[0] };
        }
        
        // Check folders
        if (collection.folders) {
          for (const folder of collection.folders) {
            if (folder.requests && folder.requests.length > 0) {
              return { project, collection, folder, subFolder: null, api: folder.requests[0] };
            }
            
            // Check sub-folders
            if (folder.sub_folders) {
              for (const subFolder of folder.sub_folders) {
                if (subFolder.requests && subFolder.requests.length > 0) {
                  return { project, collection, folder, subFolder, api: subFolder.requests[0] };
                }
              }
            }
          }
        }
      }
    }
    return null;
  };

  // Helper function to select and display an API
  const selectAndDisplayApi = async (result) => {
    const { project, collection, folder, subFolder, api } = result;
    
    // Build the full path
    let fullPath = `${project.name} / ${collection.name}`;
    if (folder) fullPath += ` / ${folder.folder_name}`;
    if (subFolder) fullPath += ` / ${subFolder.folder_name}`;
    fullPath += ` / ${api.request_name}`;

    // Set the dashboard to API tab
    setActiveDashboard("API");
    sessionStorage.setItem("tab", "API");

    // CRITICAL FIX: Fetch authorization if not available in projects structure
    let authorization = api.authorization;
    
    if (!authorization || !authorization.id) {
      try {
        const authResponse = await request(`/api/authorization/request/${api.request_id}`, "GET");
        
        if (authResponse?.success && authResponse?.data?.authorization) {
          authorization = authResponse.data.authorization;
          
          // Update the api object in projects structure for future use
          api.authorization = authorization;
        } else {
          console.warn('⚠️ [selectAndDisplayApi] No authorization found for request:', api.request_id);
        }
      } catch (error) {
        console.error('❌ [selectAndDisplayApi] Failed to fetch authorization:', error);
      }
    }

    // Set metadata to display the API
    // CRITICAL FIX: Also set authorization in request_body for compatibility with useRequestData
    const apiWithAuth = {
      ...api,
      authorization: authorization  // Ensure authorization is in request_body
    };
    
    dispatch(updateMetaData({
      active_project: project.id,
      active_collection: collection.id,
      active_folder_id: folder?.id || null,
      active_subfolder_id: subFolder?.id || null,
      request_body: apiWithAuth,  //  Use api with authorization included
      api_url: api.raw_url || "",
      request_id: api.request_id,
      api_name: api.request_name,
      active_api: api.request_id,
      active_auth: authorization,  //  Use fetched or existing authorization
      full_path: fullPath,
      api_screen: true,
      project_level: "request",
      collection_request_script: 1,
      folder_request_script: folder?.id || null,
      sub_folder_request_script: subFolder?.id || null,
    }));

    setActiveAuthorization(api);
    setPositionApi(1);
    hasAutoSelectedFirstApi.current = true;

    // Save to user-scoped localStorage for persistence across sessions
    const lastSelectedApi = {
      request_id: api.request_id,
      project_id: project.id,
      collection_id: collection.id,
      folder_id: folder?.id || null,
      subfolder_id: subFolder?.id || null,
    };
    const storageKey = getLastApiStorageKey();
    localStorage.setItem(storageKey, JSON.stringify(lastSelectedApi));

    // Expand the project hierarchy in the sidebar
    setProjects(prevProjects => 
      prevProjects.map(p => ({
        ...p,
        isOpen: p.id === project.id ? true : p.isOpen,
        collections: p.collections?.map(c => ({
          ...c,
          isOpen: p.id === project.id && c.id === collection.id ? true : c.isOpen,
          folders: c.folders?.map(f => ({
            ...f,
            isOpen: p.id === project.id && c.id === collection.id && f.id === folder?.id ? true : f.isOpen,
            sub_folders: f.sub_folders?.map(sf => ({
              ...sf,
              isOpen: p.id === project.id && c.id === collection.id && f.id === folder?.id && sf.id === subFolder?.id ? true : sf.isOpen,
            })),
          })),
        })),
      }))
    );

    return true;
  };

  // Auto-select API when projects are loaded - prioritize last selected, fallback to first API
  useEffect(() => {
    
    // Only run if projects are loaded and we haven't already auto-selected
    if (
      projects && 
      projects.length > 0 && 
      !hasAutoSelectedFirstApi.current
    ) {
      
    // Async function to handle API selection
    const autoSelectApi = async () => {
      // First, try to restore the last selected API from user-scoped localStorage
      const storageKey = getLastApiStorageKey();
      const savedApiData = localStorage.getItem(storageKey);
      
      if (savedApiData) {
        try {
          const lastApi = JSON.parse(savedApiData);
          
          const result = findApiInProjects(projects, lastApi.request_id);
          if (result) {
            await selectAndDisplayApi(result);
            return;
          }
        } catch (e) {
          console.error('Error parsing saved API data:', e);
        }
      }
      
      // Only select first API if no API is currently selected
      if (!metaData?.api_screen && !metaData?.request_id) {
        // Fallback: Select the first available API
        const firstApiResult = findFirstApiInProjects(projects);
        
        if (firstApiResult) {
          await selectAndDisplayApi(firstApiResult);
        } else {
          hasAutoSelectedFirstApi.current = true; // Mark as done even if no API found
        }
      } else {
        hasAutoSelectedFirstApi.current = true;
      }
      
      };
      
      // Execute async function
      autoSelectApi();
    }
  }, [projects]);

  // Save the selected API whenever the user manually selects one
  useEffect(() => {
    if (metaData?.request_id && metaData?.api_screen && metaData?.active_project && metaData?.active_collection) {
      const lastSelectedApi = {
        request_id: metaData.request_id,
        project_id: metaData.active_project,
        collection_id: metaData.active_collection,
        folder_id: metaData.active_folder_id || null,
        subfolder_id: metaData.active_subfolder_id || null,
      };
      const storageKey = getLastApiStorageKey();
      localStorage.setItem(storageKey, JSON.stringify(lastSelectedApi));
    }
  }, [metaData?.request_id, metaData?.api_screen, metaData?.active_project, metaData?.active_collection]);
  

  // useEffect(() => {
  //   dispatch(
  //     updateMetaData({
  //       active_project: null,
  //       active_collection: null,
  //       active_folder_id: null,
  //       active_subfolder_id: null,
  //       active_auth: null,
  //       folder_name: "",
  //       api_screen: false,
  //       api_url: null,
  //       environment_id: null,
  //       request_id: null,
  //       request_body: null,
  //       full_path: null,
  //     })
  //   );
  // }, [activeTab]);

  const [allAuthorization, setAllAuthorization] = useState([]);
  useEffect(() => {
    setTriggerHeader(true);
    if(readToken()){
      fetchProjects();
    }

    const fetchAuthorizationTypes = async () => {
      try {
        const response = await request("/api/authorization");
        setAllAuthorization(response?.data?.authorization || []);
      } catch (err) {
        console.error("Error fetching authorizations types:", err);
      }
    };

    if(readToken()) {
      fetchAuthorizationTypes();
        }
    // navigate("/")
  }, []);

  
  // serverTimeZone moved to the useServerTimeZone hook (shared/hooks) on
  // 22 Aug 2026 — the third slice out of this provider, after environments.

useEffect(() => {
    if(readToken()){
      fetchPendingRequests();
      sessionStorage.removeItem("previousResponse");
    }
  }, []);

  useEffect(() => {
    const intervalId = setInterval(() => {
      request("/api/users/setLastActive", "POST", null)
        .then((response) => {
          // resonse
        })
        .catch((error) => {
          //erro
        });
    }, 5 * 60 * 1000);

    return () => clearInterval(intervalId);
  }, []);

  // console.log({metaData})
  useEffect(() => {
    setSelectedMethod(metaData?.request_body?.method_name);
  }, [metaData?.request_body]);


  // console.log(metaData);
  // console.log("projects", projects);
  // console.log(sectionTracker)
  // console.log(metaData?.api_url);
  // console.log(editorContent);
  // console.log(headerKeyValue);

  // console.log(isLoading)
  // useEffect(()=>{
  //   if(isLoading==true)
  //   {
  //     <Loader/>
  //   }
  // },[isLoading])

  const [orchestrationType, setOrchestrationType] = useState(null);

  // The environments slice (environmentVariable, refetchEnvironmentVariables,
  // envName, selectedEnvironment) moved to EnvironmentsProvider on 22 Aug 2026;
  // serverTimeZone to the useServerTimeZone hook; and the orchestration
  // results view state (responseData, selectedRow, currentOrchestration,
  // orchestrationLoader) to the test-plans feature's TestPlansProvider.

  const [selectedOrchestration, setSelectedOrchestration] = useState(null);
  /* Memoised. This object used to be a fresh literal on every render, so all
     37 consumers re-rendered whenever anything in the provider changed —
     including on every keystroke in the API builder's request panel, because
     the header and script state lives here. Its identity now changes only when
     one of the values does.

     The 30 entries no consumer referenced went at the same time: they were 40%
     of the context's surface and each of them cost every consumer a re-render. */
  const contextValue = useMemo(() => ({
        isLoading,
        setIsLoading,
        response,
        setResponse,
        loaderStatus,
        setLoaderStatus,
        metaData,  // CRITICAL FIX: Add metaData to context
        projects,
        setProjects,
        orchestrations,
        setOrchestrations,
        datasetName,
        setSingleApiRunData,
        activeAuthorization,
        setActiveAuthorization,
        fetchProjects,
        pendingRequests,
        fetchPendingRequests,
        setHeaderKeyValue,
        activeTab,
        setActiveTab,
        allowedProjectIds,
        setallowedProjectIds,
        setCheckedRowsData,
        AuthorizationScriptDetails,
        setAuthorizationScriptDetails,
        bearerValue,
        setBearerValue,
        basicAuthValue,
        setBasicAuthValue,
        checkedRows,
        setCheckedRows,
        localRows,
        setLocalRows,
        description,
        setDescription,
        requestId,
        scriptContent,
        scriptType,
        orchestrationType,
        selectedDataset,
        selectedOrchestration,
        setSelectedOrchestration,
        appSidebarToggleRef,
        fetchOrchestrations,
  }), [AuthorizationScriptDetails, activeAuthorization, activeTab, allowedProjectIds, appSidebarToggleRef, basicAuthValue, bearerValue, checkedRows, datasetName, description, fetchOrchestrations, fetchPendingRequests, fetchProjects, isLoading, loaderStatus, localRows, metaData, orchestrationType, orchestrations, pendingRequests, projects, requestId, response, scriptContent, scriptType, selectedDataset, selectedOrchestration, setActiveAuthorization, setActiveTab, setAuthorizationScriptDetails, setBasicAuthValue, setBearerValue, setCheckedRows, setCheckedRowsData, setDescription, setHeaderKeyValue, setIsLoading, setLoaderStatus, setLocalRows, setOrchestrations, setProjects, setResponse, setSelectedOrchestration, setSingleApiRunData, setallowedProjectIds]);

  return (
    <GlobalContext.Provider
      value={contextValue}
    >
      {children}
    </GlobalContext.Provider>
  );
};
